import { useCallback } from 'react';
import { normalizeArrayFields, COMMON_ARRAY_FIELDS } from './utils/normalizeApiResponse';
import { getApiBaseUrl } from './utils/apiBaseUrl';
import { isMarkupBody, transportErrorMessage } from './utils/httpErrors';

/**
 * API Error with structured error information
 */
export interface ApiErrorDetail {
  message: string;
  code?: string;
  [key: string]: any;
}

export class ApiError extends Error {
  status?: number;
  code?: string;
  detail?: ApiErrorDetail;

  constructor(message: string, status?: number, code?: string, detail?: ApiErrorDetail) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

/**
 * Message used when a failed response carried nothing showable. Exported so
 * callers that layer their own copy on top can recognise it as a placeholder
 * instead of pattern-matching the wording.
 */
export const httpStatusPlaceholder = (status: number): string => `HTTP error! status: ${status}`;

/**
 * Return type for useApi hook
 */
export interface ApiResult {
  apiFetch: (url: string, options?: RequestInit) => Promise<Response>;
  apiFetchJson: <T>(url: string, options?: RequestInit, arrayFields?: string[]) => Promise<T>;
}

/**
 * Generic API fetch hook with type safety
 *
 * @param token - Authentication token
 * @param logout - Logout callback function
 * @returns Object with typed apiFetch function
 *
 * @example
 * const { apiFetch } = useApi(token, logout);
 * const response = await apiFetch<Customer>('/api/customers/123');
 * const data = await response.json();
 */
export const useApi = (token: string | null, logout: () => void): ApiResult => {
  const checkVersion = useCallback(async (response: Response): Promise<Response> => {
    // TEMP FIX: Disable version check until BUILD_TIMESTAMP is properly configured
    // const serverVersion = response.headers.get('X-Build-Version');
    // const clientVersion = process.env.REACT_APP_BUILD_TIMESTAMP;

    // if (serverVersion && clientVersion && serverVersion !== clientVersion) {
    //   console.log('New version detected, logging out...');
    //   window.alert("The portal has been updated. Please login again.");
    //   logout();
    //   window.location.href = '/login';
    //   return response;
    // }
    return response;
    // `logout` is referenced only by the disabled version check above.
  }, []);

  const apiFetch = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    const baseUrl = getApiBaseUrl();
    const cleanUrl = url.startsWith('/') ? url : `/${url}`; // Ensure leading slash

    // Track Amazon API calls in development only
    if (process.env.NODE_ENV !== 'production' && url.includes('amazon') && (url.includes('sync') || url.includes('fetch'))) {
      console.warn('[DEBUG] AMAZON API CALL:', {
        url: cleanUrl,
        method: options.method || 'GET',
        timestamp: new Date().toISOString(),
        hasBody: !!options.body,
        stackTrace: new Error().stack?.split('\n').slice(1, 6).join('\n')
      });
    }

    // Only add auth header if token exists and it's not a login request
    // Don't set Content-Type for FormData - browser will set it with boundary
    const isFormData = options.body instanceof FormData;
    const headers: Record<string, string> = { ...options.headers as Record<string, string> };

    if (token && !url.includes('/api/token')) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Remove Content-Type if FormData (browser will set it automatically)
    if (isFormData && headers['Content-Type']) {
      delete headers['Content-Type'];
    }

    try {
      const response = await fetch(`${baseUrl}${cleanUrl}`, {
        ...options,
        headers
      });

      await checkVersion(response);

      if (!response.ok) {
        if (response.status === 401) {
          logout();
          throw new ApiError('Unauthorized - please log in again', 401);
        }

        // Try to extract error message from response
        let errorMessage = httpStatusPlaceholder(response.status);
        let errorCode: string | undefined;
        let errorDetail: ApiErrorDetail | null = null;  // Preserve full error detail for structured errors

        // Read the body ONCE. `response.json()` consumes the stream even when
        // parsing fails, so a follow-up `response.text()` can only throw
        // "body stream already read" and every non-JSON error would keep the
        // placeholder message above.
        let rawBody = '';
        try {
          rawBody = await response.text();
        } catch {
          rawBody = '';
        }

        let errorData: unknown = null;
        if (rawBody) {
          try {
            errorData = JSON.parse(rawBody);
          } catch {
            errorData = null;
          }
        }

        if (errorData && typeof errorData === 'object') {
          const body = errorData as { detail?: unknown; error?: unknown; message?: unknown };
          if (body.detail && typeof body.detail === 'object') {
            const detail = body.detail as ApiErrorDetail;
            errorMessage = detail.message || errorMessage;
            errorCode = detail.code || errorCode;
            errorDetail = detail;  // Preserve full structured error
          } else if (typeof body.detail === 'string') {
            errorMessage = body.detail;
          } else if (typeof body.error === 'string' && body.error) {
            errorMessage = body.error;
          } else if (typeof body.message === 'string' && body.message) {
            errorMessage = body.message;
          }
        } else {
          // Nothing decodable came back, so a proxy or CDN answered rather than
          // the API. Its HTML error page must never reach the UI.
          const transportMessage = transportErrorMessage(response.status);
          if (transportMessage) {
            errorMessage = transportMessage;
          } else if (rawBody && !isMarkupBody(rawBody)) {
            errorMessage = rawBody.substring(0, 200);
          }
        }

        if (errorCode === 'PASSWORD_CHANGE_REQUIRED') {
          window.dispatchEvent(new CustomEvent('password-change-required', {
            detail: { message: errorMessage }
          }));
        }

        throw new ApiError(errorMessage, response.status, errorCode, errorDetail || undefined);
      }

      return response;
    } catch (error) {
      // Browsers word a failed request differently ("Failed to fetch" in
      // Chrome, "NetworkError..." in Firefox, "Load failed" in Safari), so
      // branch on the type rather than on the wording. A deliberate
      // cancellation is a DOMException named AbortError and must pass through
      // untouched: callers across the app swallow cancellations by checking
      // `err.name`, and renaming it would surface every unmount as an error.
      if (error instanceof TypeError) {
        console.error('Network Error:', error);
        throw new ApiError('Network error - please check your connection and try again');
      }
      console.error('API Error:', error);
      throw error;
    }
  }, [token, logout, checkVersion]);

  /**
   * Fetch JSON with automatic null→[] normalization for array fields.
   * Replaces the common pattern: const res = await apiFetch(url); const data = await res.json();
   *
   * @param arrayFields - Field names that should be arrays (null → []).
   *                       Defaults to COMMON_ARRAY_FIELDS if omitted.
   */
  const apiFetchJson = useCallback(async <T>(
    url: string,
    options: RequestInit = {},
    arrayFields?: string[]
  ): Promise<T> => {
    const response = await apiFetch(url, options);
    const data = await response.json();
    return normalizeArrayFields<T>(data, arrayFields ?? COMMON_ARRAY_FIELDS);
  }, [apiFetch]);

  return { apiFetch, apiFetchJson };
};
